"use client";

/**
 * @fileoverview Client-side Supabase authentication context provider.
 * @description This provider utilizes the `useAuth` hook to fetch and manage
 * the current user, session, and loading state from Supabase Auth. It makes this
 * state available to consuming components via React Context. Wrap your main
 * application layout or relevant client-side sections with this provider.
 * @active Client component provider.
 *
 * @see {@link src/hooks/useAuth.ts} - The core hook used internally to manage auth state.
 * @see {@link src/app/layout.tsx} - Potential place to integrate this provider.
 * @see {@link src/types/userAuth.ts} - Core authentication type definitions.
 */

import React, { createContext, useContext, ReactNode } from "react";
import { User, Session } from "@supabase/supabase-js";
import { useAuth } from "@/hooks/useAuth"; // Import the refactored hook

/**
 * Type definition for the authentication context value.
 */
interface SupabaseAuthContextType {
  user: User | null;
  session: Session | null;
  isLoading: boolean;
}

/**
 * React Context for Supabase authentication state.
 * Provides default values for initial state or when used outside the provider.
 */
const SupabaseAuthContext = createContext<SupabaseAuthContextType>({
  user: null,
  session: null,
  isLoading: true, // Default to loading initially
});

/**
 * Props for the SupabaseAuthProvider component.
 */
interface SupabaseAuthProviderProps {
  children: ReactNode;
}

/**
 * Provider component that fetches and provides Supabase auth state.
 * Uses the `useAuth` hook internally.
 *
 * @param {SupabaseAuthProviderProps} props - Component props containing children elements.
 * @returns {JSX.Element} The provider component wrapping its children with the auth context.
 * @see {@link useAuth}
 */
export const SupabaseAuthProvider: React.FC<SupabaseAuthProviderProps> = ({
  children,
}) => {
  const { user, session, isLoading } = useAuth(); // Use the refactored hook

  return (
    <SupabaseAuthContext.Provider value={{ user, session, isLoading }}>
      {children}
    </SupabaseAuthContext.Provider>
  );
};

/**
 * Custom hook to easily consume the Supabase authentication context.
 * Throws an error if used outside of a `SupabaseAuthProvider`.
 *
 * @returns {SupabaseAuthContextType} The current authentication state including user, session, and loading status.
 * @throws {Error} If the hook is called outside of a `SupabaseAuthProvider`.
 *
 * @example
 * import { useSupabaseAuth } from '@/providers/supabaseAuthProvider.client';
 *
 * function MyComponent() {
 *   const { user, session, isLoading } = useSupabaseAuth();
 *
 *   if (isLoading) return <p>Loading auth state...</p>;
 *   if (!user) return <p>Please sign in.</p>;
 *
 *   return <p>Welcome, {user.email}</p>;
 * }
 */
export const useSupabaseAuth = (): SupabaseAuthContextType => {
  const context = useContext(SupabaseAuthContext);
  if (context === undefined) {
    throw new Error(
      "useSupabaseAuth must be used within a SupabaseAuthProvider"
    );
  }
  return context;
};
